import { useState, useEffect, useRef } from "react";
import {
  buildBackup,
  triggerDownload,
  restoreFromBackup,
  getBackupStatus,
  SAVES_PER_REMINDER,
  HOURS_PER_REMINDER,
} from "./gfiBackup";
import FileDropZone from "./FileDropZone";
import { loadFromSupabase } from "../supabaseDB";
import { exportLocalToSupabase } from "../supabaseMigration";

/* Tables whose localStorage cache must be primed before a snapshot so
   the JSON we hand back includes every record in Supabase, not the
   stale subset the user's browser happens to have loaded. Mirrors the
   `MIGRATION_TASKS` list keys → legacy localStorage keys. */
const SUPABASE_TABLES = [
  ["seasons",         "gfi-seasons"],
  ["suppliers",       "gfi-suppliers"],
  ["clients",         "gfi-clients"],
  ["products",        "gfi-products"],
  ["warehouses",      "gfi-warehouses"],
  ["brokers",         "gfi-brokers"],
  ["quality_control", "gfi-quality-control"],
  ["certifiers",      "gfi-certifiers"],
  ["growers",         "gfi-growers"],
  ["certifications",  "gfi-certifications"],
  ["export_certs",    "gfi-export-certs"],
  ["fee_catalog",     "gfi-fee-catalog"],
  ["shipments",       "gfi-shipments"],
  ["shipper_shipments", "gfi-shipper-shipments"],
  ["bills",           "gfi-bills"],
  ["invoices",        "gfi-invoices"],
  ["payments",        "gfi-payments"],
  ["expense_bills",   "gfi-expense-bills"],
  ["fixed_pos",       "gfi-fixed-pos"],
  ["consignment_pos", "gfi-consignment-pos"],
];

async function primeLocalStorageFromSupabase() {
  await Promise.all(SUPABASE_TABLES.map(function (p) {
    return loadFromSupabase(p[0], { force: true }).then(function (rows) {
      try { localStorage.setItem(p[1], JSON.stringify(rows)); } catch (e) {}
    }).catch(function () {});
  }));
}

/* ════════════════════════════════════════════════════════════════════
   BackupRestore — full-app data snapshot & restore screen
   Single click on "Backup" downloads gfi-backup-YYYY-MM-DD_HHMM.json
   with every gfi-* localStorage key + IndexedDB documents.
   Restore reads such a file and writes it back (optional wipe).
   ──────────────────────────────────────────────────────────────────
   Why no automatic write to a folder on disk: browser sandboxes block
   arbitrary filesystem writes. The closest equivalent (File System
   Access API) is Chromium-only and still requires the user to grant
   a folder once per session. For now this module relies on (a) the
   one-click download here and in the navbar, and (b) a reminder
   banner the App shell shows when the save counter or 24-h clock
   triggers it.
   ════════════════════════════════════════════════════════════════════ */

const T = {
  en: {
    title: "Backup & Restore",
    subtitle: "Export every record + document to a single JSON file. Re-import to recover.",
    critical: "Critical — clearing browser cache wipes the app. Back up daily.",
    backupNow: "💾 Backup now",
    backupHint: "Downloads gfi-backup-YYYY-MM-DD_HHMM.json with every localStorage key + IndexedDB document.",
    includeDocs: "Include documents (PDFs, photos, attachments)",
    includeDocsHint: "Larger file but a true full backup. Disable for a small structural-only snapshot.",
    restore: "Restore from file",
    chooseFile: "Choose backup file…",
    selected: "Selected",
    wipeFirst: "Wipe existing data first",
    wipeFirstHint: "When ON, every gfi-* key is removed before the backup is imported. When OFF, the backup is merged over current data (existing keys overwritten, new keys added).",
    doRestore: "Restore now",
    confirmRestore: "Restore from this backup? This will overwrite current data. Continue?",
    confirmWipe: "WIPE then restore: every gfi-* localStorage key will be deleted and replaced with the backup. Continue?",
    restoring: "Restoring…",
    restored: "Restore complete. Reload to see the data.",
    reload: "Reload now",
    invalidFile: "Invalid backup file.",
    lastBackup: "Last backup",
    never: "Never",
    savesSince: "Saves since last backup",
    overdue: "OVERDUE",
    hoursAgo: "h ago",
    minutesAgo: "min ago",
    daysAgo: "d ago",
    stats: "Snapshot stats",
    lsKeys: "localStorage keys",
    idbKeys: "IndexedDB documents",
    estSize: "Estimated size",
    building: "Building backup…",
    reminderRule: "Reminder triggers after " + SAVES_PER_REMINDER + " saves or " + HOURS_PER_REMINDER + " h, whichever comes first.",
    folderNote: "💡 Browser apps can't write directly to C:\\GFI\\gfi-operations\\backups\\ — keep the downloaded files in that folder yourself, or share them to Drive/Dropbox.",
  },
  es: {
    title: "Respaldo y Restauración",
    subtitle: "Exporta todos los registros + documentos a un solo archivo JSON. Re-importa para recuperar.",
    critical: "Crítico — limpiar el caché del navegador borra la app. Respalda diario.",
    backupNow: "💾 Respaldar ahora",
    backupHint: "Descarga gfi-backup-YYYY-MM-DD_HHMM.json con todas las llaves de localStorage + documentos de IndexedDB.",
    includeDocs: "Incluir documentos (PDFs, fotos, adjuntos)",
    includeDocsHint: "Archivo más grande pero respaldo completo real. Desactiva para una captura solo estructural pequeña.",
    restore: "Restaurar desde archivo",
    chooseFile: "Elegir archivo de respaldo…",
    selected: "Seleccionado",
    wipeFirst: "Borrar datos actuales primero",
    wipeFirstHint: "Si ON, cada llave gfi-* se elimina antes de importar el respaldo. Si OFF, el respaldo se mezcla sobre los datos actuales (sobrescribe existentes, añade nuevos).",
    doRestore: "Restaurar ahora",
    confirmRestore: "¿Restaurar desde este respaldo? Esto sobrescribirá los datos actuales. ¿Continuar?",
    confirmWipe: "BORRAR y restaurar: cada llave gfi-* de localStorage se eliminará y reemplazará con el respaldo. ¿Continuar?",
    restoring: "Restaurando…",
    restored: "Restauración completa. Recarga la página para ver los datos.",
    reload: "Recargar ahora",
    invalidFile: "Archivo de respaldo inválido.",
    lastBackup: "Último respaldo",
    never: "Nunca",
    savesSince: "Guardados desde el último respaldo",
    overdue: "VENCIDO",
    hoursAgo: "h",
    minutesAgo: "min",
    daysAgo: "d",
    stats: "Estadísticas del respaldo",
    lsKeys: "llaves localStorage",
    idbKeys: "documentos IndexedDB",
    estSize: "Tamaño estimado",
    building: "Preparando respaldo…",
    reminderRule: "El recordatorio se activa tras " + SAVES_PER_REMINDER + " guardados o " + HOURS_PER_REMINDER + " h, lo que ocurra primero.",
    folderNote: "💡 Las apps web no pueden escribir directo a C:\\GFI\\gfi-operations\\backups\\ — guarda los archivos descargados en esa carpeta manualmente, o súbelos a Drive/Dropbox.",
  },
};

function fmtAgo(hours, t) {
  if (hours === Infinity || isNaN(hours)) return t.never;
  if (hours < 1) return Math.max(1, Math.round(hours * 60)) + " " + t.minutesAgo;
  if (hours < 24) return Math.round(hours) + " " + t.hoursAgo;
  return Math.round(hours / 24) + " " + t.daysAgo;
}

function fmtBytes(n) {
  if (n < 1024) return n + " B";
  if (n < 1024 * 1024) return (n / 1024).toFixed(1) + " KB";
  return (n / 1024 / 1024).toFixed(2) + " MB";
}

export default function BackupRestore({ lang: globalLang }) {
  var lang = globalLang || "es";
  var t = T[lang];

  var [includeDocs, setIncludeDocs] = useState(true);
  var [busy, setBusy] = useState(false);
  var [file, setFile] = useState(null);
  var [wipeFirst, setWipeFirst] = useState(false);
  var [msg, setMsg] = useState("");
  var [done, setDone] = useState(false);
  var [status, setStatus] = useState(function () { return getBackupStatus(); });
  var [preview, setPreview] = useState(null);
  var fileRef = useRef(null);

  useEffect(function () {
    var id = setInterval(function () { setStatus(getBackupStatus()); }, 5000);
    return function () { clearInterval(id); };
  }, []);

  var refreshStatus = function () { setStatus(getBackupStatus()); };

  var doBackup = async function () {
    if (busy) return;
    setBusy(true); setMsg(t.building);
    try {
      /* Pull every Supabase table into localStorage first so the snapshot
         reflects the authoritative cloud state, not whatever subset the
         user happens to have in this browser. */
      await primeLocalStorageFromSupabase();
      var b = await buildBackup({ includeDocs: includeDocs });
      triggerDownload(b);
      setPreview({
        lsKeys: b.counts.localStorageKeys,
        idbKeys: b.counts.indexedDBKeys,
        bytes: JSON.stringify(b).length,
        exportedAt: b.exportedAt,
      });
      setMsg("");
      refreshStatus();
    } catch (e) {
      setMsg("Error: " + (e && e.message ? e.message : String(e)));
    }
    setBusy(false);
  };

  var pickFiles = function (files) {
    var f = files && files[0];
    setFile(f || null);
    setMsg("");
    setDone(false);
  };
  var pickFile = function (e) { pickFiles(e.target.files); };

  var doRestore = async function () {
    if (!file || busy) return;
    var prompt = wipeFirst ? t.confirmWipe : t.confirmRestore;
    if (!window.confirm(prompt)) return;
    setBusy(true); setMsg(t.restoring); setDone(false);
    try {
      var text = await file.text();
      var parsed = JSON.parse(text);
      await restoreFromBackup(parsed, { wipeFirst: wipeFirst });
      /* Push the restored localStorage payload up to Supabase so the
         cloud matches the backup, not the previous state. Failures here
         do NOT mark the restore as failed — localStorage is already
         repopulated and reloads will surface the data. */
      try { await exportLocalToSupabase(); } catch (e) { console.error("[BackupRestore] supabase sync after restore failed", e); }
      setMsg(t.restored);
      setDone(true);
      refreshStatus();
    } catch (e) {
      setMsg(t.invalidFile + " (" + (e && e.message ? e.message : String(e)) + ")");
    }
    setBusy(false);
  };

  var doReload = function () { window.location.reload(); };

  var lastTxt = fmtAgo(status.hoursSince, t);
  var overdue = status.saves >= SAVES_PER_REMINDER || (status.hoursSince >= HOURS_PER_REMINDER && status.saves > 0);

  var card = { padding: 16, borderRadius: 10, border: "1px solid var(--color-border)", background: "var(--color-bg-card)", marginBottom: 14 };
  var label = { fontSize: 10, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700, marginBottom: 4 };
  var btn = { padding: "10px 22px", borderRadius: 6, border: "none", background: "var(--color-accent)", color: "var(--color-accent-text)", cursor: "pointer", fontSize: 14, fontWeight: 700 };
  var btnGhost = { padding: "8px 16px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 12, fontWeight: 600 };

  return (
    <div style={{ maxWidth: 920 }}>
      <h2 style={{ fontSize: 20, fontWeight: 700, margin: "0 0 4px" }}>{t.title}</h2>
      <p style={{ fontSize: 12, color: "var(--color-text-muted)", margin: "0 0 14px" }}>{t.subtitle}</p>

      <div style={{ padding: "10px 14px", borderRadius: 8, background: "rgba(220,38,38,0.07)", border: "1px solid rgba(220,38,38,0.25)", color: "#B91C1C", fontSize: 12, fontWeight: 600, marginBottom: 18 }}>
        ⚠ {t.critical}
      </div>

      {/* STATUS STRIP */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 14 }}>
        <div style={card}>
          <div style={label}>{t.lastBackup}</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: overdue ? "#DC2626" : "var(--color-accent)", fontFamily: "'JetBrains Mono', monospace" }}>{lastTxt}</div>
          {status.lastIso && <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginTop: 2 }}>{new Date(status.lastIso).toLocaleString(lang === "es" ? "es-MX" : "en-US")}</div>}
        </div>
        <div style={card}>
          <div style={label}>{t.savesSince}</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: status.saves >= SAVES_PER_REMINDER ? "#DC2626" : "var(--color-text)", fontFamily: "'JetBrains Mono', monospace" }}>{status.saves}</div>
          <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginTop: 2 }}>{t.reminderRule}</div>
        </div>
        <div style={{ ...card, background: overdue ? "rgba(220,38,38,0.06)" : "var(--color-bg-card)", borderColor: overdue ? "rgba(220,38,38,0.3)" : "var(--color-border)" }}>
          <div style={label}>{lang === "es" ? "Estado" : "Status"}</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: overdue ? "#DC2626" : "#047857", fontFamily: "'JetBrains Mono', monospace" }}>
            {overdue ? t.overdue : "OK"}
          </div>
        </div>
      </div>

      {/* BACKUP CARD */}
      <div style={card}>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4, color: "var(--color-accent)" }}>1. {t.backupNow.replace(/^💾\s*/, "")}</div>
        <div style={{ fontSize: 11, color: "var(--color-text-muted)", marginBottom: 12 }}>{t.backupHint}</div>

        <label style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 12, fontSize: 12, cursor: "pointer" }}>
          <input type="checkbox" checked={includeDocs} onChange={function (e) { setIncludeDocs(e.target.checked); }} style={{ marginTop: 3 }} />
          <span>
            <div style={{ fontWeight: 600 }}>{t.includeDocs}</div>
            <div style={{ fontSize: 10, color: "var(--color-text-muted)" }}>{t.includeDocsHint}</div>
          </span>
        </label>

        <button onClick={doBackup} disabled={busy} style={{ ...btn, opacity: busy ? 0.6 : 1, cursor: busy ? "not-allowed" : "pointer" }}>
          {busy ? t.building : t.backupNow}
        </button>

        {preview && (
          <div style={{ marginTop: 12, padding: "10px 12px", borderRadius: 6, background: "rgba(42,122,111,0.06)", border: "1px solid rgba(42,122,111,0.18)", fontSize: 11, fontFamily: "'JetBrains Mono', monospace" }}>
            <div style={{ fontWeight: 700, color: "var(--color-accent)", marginBottom: 4 }}>✓ {t.stats}</div>
            <div>{t.lsKeys}: <strong>{preview.lsKeys}</strong></div>
            <div>{t.idbKeys}: <strong>{preview.idbKeys}</strong></div>
            <div>{t.estSize}: <strong>{fmtBytes(preview.bytes)}</strong></div>
          </div>
        )}

        <div style={{ marginTop: 10, fontSize: 10, color: "var(--color-text-muted)", fontStyle: "italic" }}>{t.folderNote}</div>
      </div>

      {/* RESTORE CARD */}
      <div style={card}>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4, color: "var(--color-accent)" }}>2. {t.restore}</div>

        <FileDropZone onFiles={pickFiles} multiple={false} lang={lang} style={{ padding: 4, marginTop: 8, marginBottom: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <input ref={fileRef} type="file" accept="application/json,.json" onChange={pickFile} style={{ display: "none" }} />
            <button onClick={function () { if (fileRef.current) fileRef.current.click(); }} style={btnGhost}>{t.chooseFile}</button>
            {file
              ? <span style={{ fontSize: 11, fontFamily: "'JetBrains Mono', monospace", color: "var(--color-text-secondary)" }}>{t.selected}: <strong>{file.name}</strong> ({fmtBytes(file.size)})</span>
              : <span style={{ fontSize: 11, color: "var(--color-text-muted)", fontStyle: "italic" }}>{lang === "es" ? "o arrastra el JSON aquí" : "or drag the JSON here"}</span>}
          </div>
        </FileDropZone>

        <label style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 12, fontSize: 12, cursor: "pointer" }}>
          <input type="checkbox" checked={wipeFirst} onChange={function (e) { setWipeFirst(e.target.checked); }} style={{ marginTop: 3 }} />
          <span>
            <div style={{ fontWeight: 600, color: wipeFirst ? "#DC2626" : "var(--color-text)" }}>{t.wipeFirst}</div>
            <div style={{ fontSize: 10, color: "var(--color-text-muted)" }}>{t.wipeFirstHint}</div>
          </span>
        </label>

        <button onClick={doRestore} disabled={!file || busy} style={{ ...btn, background: wipeFirst ? "#DC2626" : "var(--color-accent)", opacity: (!file || busy) ? 0.5 : 1, cursor: (!file || busy) ? "not-allowed" : "pointer" }}>
          {busy ? t.restoring : t.doRestore}
        </button>

        {done && (
          <button onClick={doReload} style={{ ...btn, marginLeft: 10, background: "#047857" }}>↻ {t.reload}</button>
        )}
      </div>

      {msg && (
        <div style={{ padding: "10px 14px", borderRadius: 8, background: done ? "rgba(16,185,129,0.08)" : "rgba(59,130,246,0.08)", border: "1px solid " + (done ? "rgba(16,185,129,0.25)" : "rgba(59,130,246,0.25)"), color: done ? "#047857" : "#1D4ED8", fontSize: 12, fontWeight: 600 }}>
          {msg}
        </div>
      )}
    </div>
  );
}
